import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/auth-context'

interface ReactivationResult {
  success: boolean
  message: string
  data?: {
    token?: string
    email?: string
    firstName?: string
    lastName?: string
    user?: {
      id: string
      email: string
      firstName?: string
      lastName?: string
    }
    organization?: {
      id: string
      name: string
      role: string
    }
    organizations?: Array<{
      id: string
      name: string
      role: string
    }>
  }
  error?: string
}

type ReactivationStatus = 'loading' | 'redirecting' | 'success' | 'error' | 'expired' | 'invalid'

export function ReactivatePage() {
  const [status, setStatus] = useState<ReactivationStatus>('loading')
  const [result, setResult] = useState<ReactivationResult | null>(null)

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const { authenticateWithToken } = useAuth()

  const logo = useMemo(() => '/logo.png', [])

  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }

    confirmReactivation(token)
  }, [token])

  const confirmReactivation = async (reactivationToken: string) => {
    try {
      const response = await api.post<ReactivationResult>('/auth/confirm-reactivation', { token: reactivationToken })
      setResult(response)

      if (response.success) {
        const authToken = response.data?.token

        if (authToken) {
          setStatus('redirecting')

          try {
            await authenticateWithToken(authToken)
            navigate('/dashboard', { replace: true })
            return
          } catch (authError) {
            console.error('Reactivation auth error', authError)
            setStatus('error')
            setResult({
              success: false,
              message: 'Your account was reactivated, but we could not complete the sign-in. Please try logging in manually.',
              error: 'AUTHENTICATION_FAILED',
            })
            return
          }
        }

        setStatus('success')
      } else if (response.error?.includes('expired')) {
        setStatus('expired')
      } else {
        setStatus('error')
      }
    } catch (err) {
      console.error('Reactivation confirmation error', err)
      setStatus('error')
      setResult({
        success: false,
        message: 'Failed to confirm reactivation. Please try again.',
        error: 'Network error',
      })
    }
  }

  const handleReturnToLogin = () => {
    navigate('/login')
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
      case 'redirecting':
        return <Loader2 className="w-12 h-12 text-primary animate-spin" />
      case 'success':
        return <CheckCircle className="w-12 h-12 text-green-600" />
      case 'error':
      case 'expired':
      case 'invalid':
        return <XCircle className="w-12 h-12 text-destructive" />
      default:
        return null
    }
  }

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Reactivating your account...'
      case 'redirecting':
        return 'Account reactivated!'
      case 'success':
        return 'Account reactivated successfully!'
      case 'expired':
        return 'Reactivation link expired'
      case 'invalid':
        return 'Invalid reactivation link'
      case 'error':
        return 'Reactivation failed'
      default:
        return ''
    }
  }

  const getStatusDescription = () => {
    switch (status) {
      case 'loading':
        return 'Please wait while we reactivate your account.'
      case 'redirecting':
        return 'Your account is active again. We are signing you in and redirecting you to your dashboard.'
      case 'success':
        if (!result?.data) {
          return 'Your account has been reactivated. You can now log in.'
        }

        const displayName =
          result.data.firstName ??
          result.data.user?.firstName ??
          result.data.user?.email ??
          result.data.email ??
          'there'

        return `Welcome back, ${displayName}! Your account has been reactivated. You can now log in.`
      case 'expired':
        return 'This reactivation link has expired. Please request a new reactivation email.'
      case 'invalid':
        return 'This reactivation link is invalid or has already been used.'
      case 'error':
        return result?.message || 'We were unable to reactivate your account. Please try again.'
      default:
        return ''
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/20 via-background to-muted/30 flex items-center justify-center p-6">
      <Card className="w-full max-w-xl shadow-xl border-border/60">
        <CardHeader className="text-center space-y-4">
          <div className="inline-flex items-center gap-3 mb-2">
            <img src={logo} alt="Gathio Logo" className="w-8 h-8" />
            <span className="text-xl font-bold text-foreground">gathio</span>
          </div>
          <div className="flex justify-center">{getStatusIcon()}</div>
          <CardTitle className="text-3xl font-semibold text-foreground tracking-tight">
            {getStatusTitle()}
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground leading-relaxed">
            {getStatusDescription()}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {(status === 'success' || status === 'redirecting') && result?.data && (
            <div className="bg-success/10 border border-success/30 rounded-xl p-4 text-sm text-success">
              <p className="font-medium">
                Reactivated for {result.data.email ?? result.data.user?.email ?? 'your account'}
              </p>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'invalid') && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 text-sm text-destructive">
              <p className="font-medium">Need help? Contact support or request a new reactivation email from your admin.</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={handleReturnToLogin} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Button>
            {(status === 'expired' || status === 'invalid') && (
              <Button onClick={() => navigate('/login?show_reactivation=true')} variant="default">
                Request New Link
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ReactivatePage
